import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Clock, ShieldCheck, ArrowRight, Sun, Moon, Battery, Brain, X } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay
}} data-id="element-80">
    {children}
  </motion.div>;
export function SalesPage() {
  const navigate = useNavigate();
  const [modalContent, setModalContent] = useState<'privacy' | 'terms' | null>(null);
  const handleBuyClick = () => {
    window.location.href = 'https://globaldigitales.com/cart/51551736758562:1';
  };
  return <div className="min-h-screen bg-cream selection:bg-gold selection:text-white" data-id="element-81">
      {/* HEADER / HERO */}
      <header className="relative pt-20 pb-24 overflow-hidden" data-id="element-82">
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-83">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} data-id="element-84">
            <span className="inline-block py-1 px-3 rounded-full bg-ivory border border-gold/30 text-gold-dark text-sm font-medium tracking-wider mb-6 uppercase" data-id="element-85">
              Para mujeres 40+
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brown leading-tight mb-6" data-id="element-86">
              Cómo "Resetear" tu Biología para Sentirte de 30 a los 45 en solo
              21 Días
            </h1>
            <p className="text-lg md:text-xl text-brown/80 max-w-2xl mx-auto mb-10 font-light leading-relaxed" data-id="element-87">
              Sin Suplementos, Dietas Extremas ni Rutinas Agotadoras. El primer
              sistema de desinflamación celular diseñado específicamente para el
              ritmo biológico de la mujer moderna.
            </p>

            <div className="relative max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-elegant mb-12" data-id="element-88">
              <img src="https://cdn.magicpatterns.com/uploads/x3WiAzVteMbEJLJskYpzcU/Gemini_Generated_Image_ptb22vptb22vptb2.png" alt="Representación del ritmo circadiano" className="w-full h-auto object-cover" data-id="element-89" />
              <div className="absolute inset-0 bg-gradient-to-t from-brown/40 to-transparent" data-id="element-90"></div>
            </div>

            <button onClick={handleBuyClick} className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-gold hover:bg-gold-dark transition-all duration-300 rounded-full shadow-lg hover:shadow-xl overflow-hidden" data-id="element-91">
              <span className="relative z-10 flex items-center gap-2" data-id="element-92">
                SÍ, QUIERO EL PROTOCOLO COMPLETO POR $17
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-93" />
              </span>
            </button>
            <p className="mt-4 text-sm text-brown/60 flex items-center justify-center gap-2" data-id="element-94">
              <ShieldCheck className="w-4 h-4 text-gold" data-id="element-95" />
              Acceso digital instantáneo. Pago seguro y cifrado.
            </p>
          </motion.div>
        </div>
      </header>

      {/* SECCIÓN 1: EL GANCHO */}
      <section className="py-20 bg-ivory" data-id="element-96">
        <div className="max-w-3xl mx-auto px-6" data-id="element-97">
          <FadeIn data-id="element-98">
            <h2 className="text-3xl md:text-4xl font-serif text-center mb-10 text-brown" data-id="element-99">
              ¿Te despertaste hoy a las 3:00 AM sintiendo que tu cuerpo te está
              fallando?
            </h2>
            <div className="space-y-6 text-lg text-brown/80 leading-relaxed" data-id="element-100">
              <p data-id="element-101">
                Si estás cansada de estar cansada, de luchar contra una
                inflamación abdominal que no cede y de sentir que la "niebla
                mental" domina tus tardes...
              </p>
              <p className="font-medium text-terracotta text-xl text-center py-4" data-id="element-102">
                No es tu edad. Es tu reloj biológico que está desfasado.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECCIÓN 2: EL PROBLEMA */}
      <section className="py-24" data-id="element-103">
        <div className="max-w-4xl mx-auto px-6" data-id="element-104">
          <FadeIn data-id="element-105">
            <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-106">
              <div data-id="element-107">
                <h2 className="text-3xl md:text-4xl font-serif mb-6 text-brown" data-id="element-108">
                  La trampa de la vida moderna
                </h2>
                <p className="text-lg text-brown/80 mb-6 leading-relaxed" data-id="element-109">
                  Vivir a los 45 años en 2026 bajo luces LED constantes, estrés
                  digital y horarios de comida caóticos ha roto tu comunicación
                  celular.
                </p>
                <p className="text-lg text-brown/80 mb-6 leading-relaxed" data-id="element-110">
                  Tu cuerpo ya no sabe cuándo quemar grasa y cuándo repararse.
                  El resultado es el envejecimiento acelerado y el agotamiento
                  crónico.
                </p>
                <div className="bg-terracotta/10 border-l-4 border-terracotta p-6 rounded-r-lg" data-id="element-111">
                  <p className="text-brown font-medium italic" data-id="element-112">
                    "Si no ajustas tu ritmo hoy, mañana estarás más dependiente
                    de fármacos y con menos vitalidad."
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-113">
                <div className="bg-ivory p-6 rounded-xl text-center shadow-sm" data-id="element-114">
                  <Moon className="w-8 h-8 text-gold mx-auto mb-3" data-id="element-115" />
                  <h3 className="font-serif text-lg mb-2" data-id="element-116">Insomnio</h3>
                  <p className="text-sm text-brown/70" data-id="element-117">
                    Despertares de madrugada
                  </p>
                </div>
                <div className="bg-ivory p-6 rounded-xl text-center shadow-sm" data-id="element-118">
                  <Battery className="w-8 h-8 text-gold mx-auto mb-3" data-id="element-119" />
                  <h3 className="font-serif text-lg mb-2" data-id="element-120">Agotamiento</h3>
                  <p className="text-sm text-brown/70" data-id="element-121">Cansancio crónico</p>
                </div>
                <div className="bg-ivory p-6 rounded-xl text-center shadow-sm" data-id="element-122">
                  <Brain className="w-8 h-8 text-gold mx-auto mb-3" data-id="element-123" />
                  <h3 className="font-serif text-lg mb-2" data-id="element-124">Niebla Mental</h3>
                  <p className="text-sm text-brown/70" data-id="element-125">
                    Falta de concentración
                  </p>
                </div>
                <div className="bg-ivory p-6 rounded-xl text-center shadow-sm" data-id="element-126">
                  <Clock className="w-8 h-8 text-gold mx-auto mb-3" data-id="element-127" />
                  <h3 className="font-serif text-lg mb-2" data-id="element-128">Metabolismo</h3>
                  <p className="text-sm text-brown/70" data-id="element-129">Inflamación constante</p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECCIÓN 3: LA SOLUCIÓN */}
      <section className="py-24 bg-brown text-cream relative overflow-hidden" data-id="element-130">
        <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none" data-id="element-131">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full border border-cream" data-id="element-132"></div>
          <div className="absolute bottom-[-10%] left-[-5%] w-[300px] h-[300px] rounded-full border border-cream" data-id="element-133"></div>
        </div>

        <div className="max-w-3xl mx-auto px-6 text-center relative z-10" data-id="element-134">
          <FadeIn data-id="element-135">
            <Sun className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-136" />
            <h2 className="text-3xl md:text-5xl font-serif mb-8 text-cream" data-id="element-137">
              He creado El Protocolo de Desinflamación Circadiana
            </h2>
            <p className="text-xl text-cream/80 mb-8 font-light leading-relaxed" data-id="element-138">
              No es una dieta; es un mapa de sincronización. Aprenderás a usar
              la luz solar, la temperatura y ventanas de tiempo para que tus
              hormonas trabajen PARA ti.
            </p>
            <p className="text-lg text-gold font-medium" data-id="element-139">
              Es el método más natural y económico para recuperar tu energía.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* SECCIÓN 4: QUÉ INCLUYE */}
      <section className="py-24 bg-ivory" data-id="element-140">
        <div className="max-w-5xl mx-auto px-6" data-id="element-141">
          <FadeIn data-id="element-142">
            <h2 className="text-3xl md:text-4xl font-serif text-center mb-16 text-brown" data-id="element-143">
              ¿Qué incluye tu acceso hoy?
            </h2>

            <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-144">
              <div className="order-2 md:order-1" data-id="element-145">
                <p className="text-xl font-medium text-terracotta mb-8" data-id="element-146">
                  Por un único pago de $17, recibes el paquete completo de
                  rescate biológico:
                </p>

                <ul className="space-y-6" data-id="element-147">
                  {[{
                  title: 'Guía Maestra: El Protocolo Circadiano 40+',
                  desc: '(60 pág) El manual paso a paso.'
                }, {
                  title: 'Bonus 1: Checklist de Reseteo del Sueño Delta',
                  desc: 'Para dormir profundamente.'
                }, {
                  title: 'Bonus 2: Plan de Comidas Anti-Inflamatorias',
                  desc: 'Recetas de 15 minutos.'
                }, {
                  title: 'Bonus 3: Rutina de Movimiento Linfático',
                  desc: 'Desinflama tu cuerpo al despertar.'
                }, {
                  title: 'Bonus 4: Guía de Detox Digital y Dopamina',
                  desc: 'Recupera tu enfoque.'
                }].map((item, i) => <li key={i} className="flex gap-4 items-start" data-id="element-148">
                      <div className="mt-1 bg-gold/20 p-1 rounded-full" data-id="element-149">
                        <CheckCircle2 className="w-5 h-5 text-gold-dark" data-id="element-150" />
                      </div>
                      <div data-id="element-151">
                        <h4 className="font-serif text-lg font-medium text-brown" data-id="element-152">
                          {item.title}
                        </h4>
                        <p className="text-brown/70 text-sm" data-id="element-153">{item.desc}</p>
                      </div>
                    </li>)}
                </ul>
              </div>

              <div className="order-1 md:order-2" data-id="element-154">
                <img src="https://cdn.magicpatterns.com/uploads/76SyMP4BbeQhGyCLJ1CrLg/Gemini_Generated_Image_aek4ypaek4ypaek4.png" alt="El Protocolo Circadiano en dispositivos" className="w-full h-auto rounded-xl shadow-elegant" data-id="element-155" />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECCIÓN 5: GARANTÍA */}
      <section className="py-20" data-id="element-156">
        <div className="max-w-3xl mx-auto px-6" data-id="element-157">
          <FadeIn data-id="element-158">
            <div className="bg-white border border-gold/20 rounded-2xl p-8 md:p-12 text-center shadow-elegant relative overflow-hidden" data-id="element-159">
              <div className="absolute top-0 left-0 w-full h-2 bg-gold" data-id="element-160"></div>
              <ShieldCheck className="w-16 h-16 text-gold mx-auto mb-6" data-id="element-161" />
              <h2 className="text-2xl md:text-3xl font-serif mb-4 text-brown" data-id="element-162">
                Garantía de 7 Días
              </h2>
              <p className="text-lg text-brown/80 leading-relaxed" data-id="element-163">
                Si en un mes no duermes mejor y te ves menos inflamada, te
                devuelvo el 100% de tu dinero.{' '}
                <span className="font-medium text-brown" data-id="element-164">Sin preguntas.</span>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CIERRE Y CTA */}
      <section className="py-24 bg-brown text-cream text-center" data-id="element-165">
        <div className="max-w-3xl mx-auto px-6" data-id="element-166">
          <FadeIn data-id="element-167">
            <h2 className="text-3xl md:text-4xl font-serif mb-8" data-id="element-168">
              Es hora de dejar de luchar contra tu naturaleza y empezar a usarla
              a tu favor.
            </h2>

            <button onClick={handleBuyClick} className="group relative inline-flex items-center justify-center px-8 py-5 text-xl font-medium text-brown bg-gold hover:bg-white transition-all duration-300 rounded-full shadow-[0_0_30px_rgba(196,149,106,0.3)] hover:shadow-[0_0_40px_rgba(255,255,255,0.4)] overflow-hidden w-full md:w-auto" data-id="element-169">
              <span className="relative z-10 flex items-center gap-2" data-id="element-170">
                SÍ, QUIERO EL PROTOCOLO COMPLETO POR $17
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-171" />
              </span>
            </button>
            <div className="mt-6 flex flex-col items-center justify-center gap-2 text-sm text-cream/60" data-id="element-172">
              <div className="flex gap-2" data-id="element-173">
                <ShieldCheck className="w-4 h-4" data-id="element-174" />
                <span data-id="element-175">Acceso digital instantáneo. Pago seguro y cifrado.</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-brown border-t border-cream/10 text-center" data-id="element-176">
        <div className="flex items-center justify-center gap-6 text-sm text-cream/50" data-id="element-177">
          <button onClick={() => setModalContent('privacy')} className="hover:text-gold transition-colors" data-id="element-178">
            Política de Privacidad
          </button>
          <span className="text-cream/20" data-id="element-179">|</span>
          <button onClick={() => setModalContent('terms')} className="hover:text-gold transition-colors" data-id="element-180">
            Términos y Condiciones
          </button>
        </div>
        <p className="mt-3 text-xs text-cream/30" data-id="element-181">
          © {new Date().getFullYear()} Todos los derechos reservados.
        </p>
      </footer>

      {/* Modal */}
      {modalContent && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-182">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="relative bg-cream text-brown rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8" data-id="element-183">
            <button onClick={() => setModalContent(null)} className="absolute top-4 right-4 text-brown/50 hover:text-brown transition-colors" data-id="element-184">
              <X className="w-6 h-6" data-id="element-185" />
            </button>

            {modalContent === 'privacy' ? <div data-id="element-186">
                <h2 className="text-2xl font-serif mb-6" data-id="element-187">
                  Política de Privacidad
                </h2>
                <div className="space-y-4 text-sm leading-relaxed text-brown/80" data-id="element-188">
                  <p data-id="element-189">
                    Tu privacidad es importante para nosotros. Esta política
                    describe cómo recopilamos, usamos y protegemos tu
                    información personal.
                  </p>
                  <h3 className="font-semibold text-brown text-base" data-id="element-190">
                    Información que Recopilamos
                  </h3>
                  <p data-id="element-191">
                    Recopilamos información que nos proporcionas directamente,
                    como tu nombre y correo electrónico al realizar una compra.
                    También recopilamos datos de uso anónimos para mejorar
                    nuestro servicio.
                  </p>
                  <h3 className="font-semibold text-brown text-base" data-id="element-192">
                    Uso de la Información
                  </h3>
                  <p data-id="element-193">
                    Utilizamos tu información para procesar tu compra, enviarte
                    el producto digital y comunicarnos contigo sobre tu pedido.
                    No vendemos ni compartimos tu información personal con
                    terceros.
                  </p>
                  <h3 className="font-semibold text-brown text-base" data-id="element-194">
                    Seguridad
                  </h3>
                  <p data-id="element-195">
                    Implementamos medidas de seguridad estándar de la industria
                    para proteger tu información personal contra acceso no
                    autorizado, alteración o destrucción.
                  </p>
                  <h3 className="font-semibold text-brown text-base" data-id="element-196">
                    Cookies
                  </h3>
                  <p data-id="element-197">
                    Utilizamos cookies para mejorar tu experiencia de navegación
                    y analizar el tráfico del sitio. Puedes desactivar las
                    cookies en la configuración de tu navegador.
                  </p>
                </div>
              </div> : <div data-id="element-198">
                <h2 className="text-2xl font-serif mb-6" data-id="element-199">
                  Términos y Condiciones
                </h2>
                <div className="space-y-4 text-sm leading-relaxed text-brown/80" data-id="element-200">
                  <p data-id="element-201">
                    Al acceder y utilizar este sitio web y adquirir nuestros
                    productos, aceptas los siguientes términos y condiciones.
                  </p>
                  <h3 className="font-semibold text-brown text-base" data-id="element-202">
                    Producto Digital
                  </h3>
                  <p data-id="element-203">
                    Este es un producto digital. Una vez completada la compra,
                    recibirás acceso instantáneo al contenido. Debido a la
                    naturaleza digital del producto, todas las ventas están
                    sujetas a nuestra política de garantía.
                  </p>
                  <h3 className="font-semibold text-brown text-base" data-id="element-204">
                    Garantía de Devolución
                  </h3>
                  <p data-id="element-205">
                    Ofrecemos una garantía de devolución de 7 días. Si no estás
                    satisfecho con el producto, puedes solicitar un reembolso
                    completo dentro de los 7 días posteriores a la compra.
                  </p>
                  <h3 className="font-semibold text-brown text-base" data-id="element-206">
                    Propiedad Intelectual
                  </h3>
                  <p data-id="element-207">
                    Todo el contenido del producto está protegido por derechos
                    de autor. No está permitido reproducir, distribuir o
                    compartir el material sin autorización expresa.
                  </p>
                  <h3 className="font-semibold text-brown text-base" data-id="element-208">
                    Limitación de Responsabilidad
                  </h3>
                  <p data-id="element-209">
                    Este producto es de carácter informativo y educativo. No
                    sustituye el consejo médico profesional. Los resultados
                    pueden variar según cada individuo.
                  </p>
                </div>
              </div>}
          </motion.div>
        </div>}
    </div>;
}